import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import dropin from 'braintree-web-drop-in';

const TOKENIZATION_KEY = import.meta.env.VITE_BRAINTREE_TOKENIZATION_KEY || 'sandbox_x6ww5pjn_ndydks477wggqytb';

export const BraintreePayment = forwardRef(function BraintreePayment({ onNonce, onError, onReady, selectedMethod }, ref) {
  const containerRef = useRef(null);
  const instanceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useImperativeHandle(ref, () => ({
    requestNonce: async () => {
      if (!instanceRef.current) {
        throw new Error('Payment form not ready');
      }
      const { nonce, type, details } = await instanceRef.current.requestPaymentMethod();
      onNonce && onNonce({ nonce, type, details });
      return { nonce, type, details };
    }
  }));

  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;

    const initDropin = async () => {
      if (instanceRef.current) {
        try { await instanceRef.current.teardown(); } catch (e) { /* ignore */ }
        instanceRef.current = null;
      }

      setLoading(true);
      setError(null);

      const options = {
        authorization: TOKENIZATION_KEY,
        container: containerRef.current,
        card: selectedMethod === 'braintree' ? {} : false,
        paypal: selectedMethod === 'braintree_paypal' ? { flow: 'checkout', amount: '0.01', currency: 'USD' } : false,
        applePay: false,
        googlePay: false,
        venmo: false,
        paypalCredit: false
      };

      if (selectedMethod === 'braintree_ach') {
        options.usBankAccount = {
          displayName: 'D2P Portal',
          bankLogin: { displayName: 'D2P Portal' }
        };
        options.card = false;
        options.paypal = false;
      }

      try {
        const instance = await dropin.create(options);
        if (cancelled) {
          instance.teardown();
          return;
        }
        instanceRef.current = instance;
        setLoading(false);
        onReady && onReady(true);
      } catch (err) {
        if (!cancelled) {
          setLoading(false);
          const msg = err.message || 'Failed to load payment form';
          setError(msg);
          onError && onError(msg);
        }
      }
    };

    initDropin();

    return () => {
      cancelled = true;
      if (instanceRef.current) {
        try { instanceRef.current.teardown(); } catch (e) { /* ignore */ }
        instanceRef.current = null;
      }
    };
  }, [selectedMethod]);

  return (
    <div style={{ marginTop: 16 }}>
      {loading && (
        <div style={{
          padding: 24,
          textAlign: 'center',
          color: '#6B7280',
          fontSize: 14
        }}>
          Loading payment form...
        </div>
      )}
      {error && (
        <div style={{
          padding: 16,
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: 8,
          fontSize: 14,
          color: '#991B1B'
        }}>
          {error}
        </div>
      )}
      <div ref={containerRef} style={{ display: loading ? 'none' : 'block' }} />
    </div>
  );
});
